import { createTheme, ThemeOptions } from '@mui/material/styles';
import * as Color from '@mui/material/colors';
import { checkboxClasses, radioClasses } from '@mui/material';


declare module '@mui/material/styles' {
   interface TypographyVariants {
      bold: React.CSSProperties;
      error: React.CSSProperties;
      italic: React.CSSProperties;
      superscript: React.CSSProperties;
      subscript: React.CSSProperties;
      list: React.CSSProperties;
      table: React.CSSProperties;
   }

   // allow configuration using `createTheme`
   interface TypographyVariantsOptions {
      bold?: React.CSSProperties;
      error?: React.CSSProperties;
      italic?: React.CSSProperties;
      superscript?: React.CSSProperties;
      subscript?: React.CSSProperties;
      list?: React.CSSProperties;
      table?: React.CSSProperties;
   }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
   interface TypographyPropsVariantOverrides {
      bold: true;
      error: true;
      italic: true;
      superscript: true;
      subscript: true;
      list: true;
      table: true;
   }
}


export const lightText = '#FFFFFF';
export const darkText = '#448FD1';
export const mediumBlue = '#196096';
export const white = '#FFFFFF';
export const darkBlue = '#084A79';
export const lightGrey = '#D7D9DA';
export const bgGridEven = Color.grey[200];
export const bgGridOdd = '#FFFFFF';
// Lightened/adjusted accent derived from mediumBlue for better contrast on dark backgrounds
export const darkAccent = '#4DA3DC';
export const darkAccentHover = '#6FB3E0';
export const darkSurface = Color.grey[800];
export const darkDivider = Color.grey[700];
export const darkbgGridEven = Color.grey[800];
export const darkbgGridOdd = Color.grey[700];

const darkThemeOverrides : ThemeOptions = {
   palette: {
      mode: 'dark',
      primary: {
         main: darkAccent
      },
      divider: darkDivider,
      background: {
         default: Color.grey[900],
         paper: Color.grey[800],
      },
      text: {
         primary: Color.grey[50],
         secondary: Color.grey[500],
      },
   },
   typography: {
       body1: {
         fontSize: '1.125rem',
         lineHeight: '1.55556',
         letterSpacing: '0.0125rem',
         textRendering: 'optimizeLegibility'
      },
      bold: {
         fontWeight: 'bold',
      },
      error: {
         color: '#FF6B6B',
         fontWeight: 'bold'
      },
      italic: {
         fontWeight: 'bold',
         fontStyle: 'italic'
      },
      superscript: {
         verticalAlign: 'super',
         fontSize: '0.675rem',
         lineHeight: '1.55556',
         letterSpacing: '0.0125rem',
         textRendering: 'optimizeLegibility'
      },
      subscript: {
         verticalAlign: 'sub',
         fontSize: '0.675rem',
         lineHeight: '1.55556',
         letterSpacing: '0.0125rem',
         textRendering: 'optimizeLegibility'
      },
      list: {
         marginBottom: "0"
      },
      table: {
         marginBottom: "0",
         paddingLeft: "0.5em",
         paddingRight: "0.5em"
      }
   },
   components: {
      MuiCssBaseline: {
         styleOverrides: `
               a { 
                  text-decoration: none;
               }
               a:link, a:visited, a:hover, a:active  { 
                  color: ${darkAccent};
               }
               a.buttonLink {
                  color: ${lightText} !important;
               }
               html {
                  scroll-behavior: smooth;
               }
               span {
                  white-space: pre-wrap;
               }
               footer {
                  background-color: ${darkSurface};
               }
            `
      },
      MuiContainer: {
         styleOverrides: {
            root: {
               '@media (min-width:1280px)': {
                  maxWidth: 'none'
               },
               '@media (min-width:600px)': {
                  paddingLeft: '0px',
                  paddingRight: '0px'
               }
            },
         },
      },
      MuiButton: {
         styleOverrides: {
              root: {
               color: lightText,
               backgroundColor: darkAccent,
               borderRadius: '0px',
               margin: '0px',
               minHeight: '0px',
               textTransform: 'unset',
               '&:disabled': {
                  backgroundColor: Color.grey[700]
               },
               '&:hover': {
                  backgroundColor: darkAccentHover,
               },
               '&:link, &:visited, &:hover, &:active': {
                  color: `${lightText} !important`
               },
               '& a': {
                  color: `${lightText} !important`
               },
               '& .MuiSvgIcon-root': {
                  color: lightText
               }
            }
         }
      },
      MuiTableCell: {
         styleOverrides: {
            head: {
               backgroundColor: darkSurface
            }
         }
      },
      MuiTable: {
         styleOverrides: {
            root: {
               borderBottom: `3px solid ${darkDivider}`
            }
         }
      },
      MuiToolbar: {
         styleOverrides: {
            root: {
               background: darkSurface
            }
         }
      },
      MuiCheckbox: {
         styleOverrides: {
            root: {
               color: darkAccent,
               [`&.${checkboxClasses.checked}`]: {
                  color: darkAccent,
               }
            }
         }
      },
      MuiRadio: {
         styleOverrides: {
            root: {
               color: darkAccent,
               [`&.${radioClasses.checked}`]: {
                  color: darkAccent,
               }
            }
         }
       },
       MuiGrid2: {
           styleOverrides: {
               root: {
                   // Use CSS vars for theming-friendly colors
                   '--Grid-oddBg': darkbgGridEven,
                   '--Grid-evenBg': darkbgGridOdd,
               },
           },
       },
   }
};

const lightThemeOverrides : ThemeOptions = {
   palette: {
      mode: 'light',
      primary: {
         main: white
      },
      divider: darkBlue,
      text: {
         primary: '#000000',
         secondary: Color.grey[800],
      }
   },
   typography: {
       body1: {
         fontSize: '1.125rem',
         lineHeight: '1.55556',
         letterSpacing: '0.0125rem',
         textRendering: 'optimizeLegibility'
      },
      bold: {
         fontWeight: 'bold',
      },
      error: {
         color: 'red',
         fontWeight: 'bold'
      },
      italic: {
         fontWeight: 'bold',
         fontStyle: 'italic'
      },
      superscript: {
         verticalAlign: 'super',
         fontSize: '0.675rem',
         lineHeight: '1.55556',
         letterSpacing: '0.0125rem',
         textRendering: 'optimizeLegibility'
      },
      subscript: {
         verticalAlign: 'sub',
         fontSize: '0.675rem',
         lineHeight: '1.55556',
         letterSpacing: '0.0125rem',
         textRendering: 'optimizeLegibility'
      },
      list: {
         marginBottom: "0"
      },
      table: {
         marginBottom: "0",
         paddingLeft: "0.5em",
         paddingRight: "0.5em"
      }
   },
   components: {
      MuiCssBaseline: {
         styleOverrides: `    
               a { 
                  text-decoration: none;
               }
               a:link, a:visited, a:hover, a:active  { 
                  color: ${darkText};
               }
               a.buttonLink {
                  color: ${lightText} !important;
               }
               html {
                  scroll-behavior: smooth;
               }
               span {
                  white-space: pre-wrap;
               }
               footer {
                  background-color: ${mediumBlue};
               }
            `
      },
      MuiContainer: {
         styleOverrides: {
            root: {
               '@media (min-width:1280px)': {
                  maxWidth: 'none'
               },
               '@media (min-width:600px)': {
                  paddingLeft: '0px',
                  paddingRight: '0px'
               }
            },
         },
      },
      MuiButton: {
         styleOverrides: {
            root: {
               backgroundColor: mediumBlue,
               borderRadius: '0px',
               margin: '0px',
               minHeight: '0px',
               textTransform: 'unset',
               '&:disabled': {
                  backgroundColor: lightGrey
               },
               '&:hover': {
                  backgroundColor: '#4DA3DC'
               },
               '&:link, &:visited, &:hover, &:active': {
                  color: `${lightText} !important`
               },
               '& a': {
                  color: `${lightText} !important`
               },
               '& .MuiSvgIcon-root': {
                  color: lightText
               }
            }
         }
      },
      MuiTableCell: {
         styleOverrides: {
            head: {
               backgroundColor: lightGrey
            }
         }
      },
      MuiTable: {
         styleOverrides: {
            root: {
               borderBottom: `3px solid ${lightGrey}`
            }
         }
      },

      MuiToolbar: {
         styleOverrides: {
            root: {
               background: mediumBlue
            }
         }
      },

      MuiCheckbox: {
         styleOverrides: {
            root: {
               color: mediumBlue,
               [`&.${checkboxClasses.checked}`]: {
                  color: mediumBlue,
               }
            }
         }
      },

      MuiRadio: {
         styleOverrides: {
            root: {
               color: mediumBlue,
               [`&.${radioClasses.checked}`]: {
                  color: mediumBlue,
               }
            }
         }
       },
       MuiGrid2: {
           styleOverrides: {
               root: {
                   // Use CSS vars for theming-friendly colors
                   '--Grid-oddBg': bgGridEven,
                   '--Grid-evenBg': bgGridOdd,
               },
           },
       },
   }
};

const themeOverides = (mode: string): ThemeOptions => ({
   spacing: 1,
   ...((mode === 'light') ? lightThemeOverrides : darkThemeOverrides)
});

export const lightTheme = createTheme(themeOverides('light'));
export const darkTheme = createTheme(themeOverides('dark'));

export default lightTheme;
